import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { unparse } from 'papaparse';
import { UserPlus, Plus } from 'lucide-react';

import { apiAdminwaitingListsGetCollection } from '@r/api/generated/client.ts';
import type {
  WaitingListWaitingListReadModelStatus,
  ApiAdminwaitingListsGetCollectionParams
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { downloadCSV } from '@r/utils/utils.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { Button } from '@r/components/ui/button.tsx';
import { Card, CardContent } from '@r/components/ui/card.tsx';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';

import {
  isWaitingListRowEditable,
  useWaitingListColumns,
  useWaitingListFilterFields,
  type WaitingListTableRow
} from '@admin/components/company/waiting-list/waiting-list-columns.tsx';
import { WaitingListProposeForm } from '@admin/components/company/waiting-list/waiting-list-propose-form.tsx';
import { WaitingListEditModal } from '@admin/components/company/waiting-list/waiting-list-edit-modal.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type WaitingListStatus =
  (typeof WaitingListWaitingListReadModelStatus)[keyof typeof WaitingListWaitingListReadModelStatus];

export const CompanyWaitingList = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const [isExporting, setIsExporting] = useState(false);
  const [isProposeFormOpen, setIsProposeFormOpen] = useState(false);
  const [proposeFormKey, setProposeFormKey] = useState(0);
  const [editWaitingListId, setEditWaitingListId] = useState<number | null>(null);

  const activeWorkspace = user.data
    ? getActiveWorkspaceUniversal(user.data.visibleWorkspaces)
    : undefined;
  const canEditWaitingList = ['ROLE_SALES_ADMIN', 'ROLE_ADMIN', 'ROLE_FIDDY_ADMIN'].includes(
    activeWorkspace?.maxRole ?? ''
  );

  const canManageWaitingListConversion = ['ROLE_ADMIN', 'ROLE_FIDDY_ADMIN'].includes(
    activeWorkspace?.maxRole ?? ''
  );

  async function waitingListFiltersQuery(
    filters: {
      firstname: string;
      lastname: string;
      email: string;
      companyName: string;
      createdByUniqueCode: string;
      createdAtAfter: string;
      createdAtBefore: string;
      status: WaitingListStatus[] | undefined;
    },
    page: number | null = null,
    limit: number | null = null
  ) {
    const {
      firstname,
      lastname,
      email,
      companyName,
      createdByUniqueCode,
      createdAtAfter,
      createdAtBefore,
      status
    } = filters;

    const orderParams: Partial<Record<'order[createdAt]', 'asc' | 'desc'>> = (() => {
      const firstSort = sorting?.[0];
      if (firstSort?.id !== 'createdAt') return {};

      const direction: 'asc' | 'desc' = firstSort.desc ? 'desc' : 'asc';
      return { 'order[createdAt]': direction };
    })();

    const params = {
      page: page ?? pageIndex + 1,
      perPage: limit ?? pageSize,
      ...(firstname ? { firstname } : {}),
      ...(lastname ? { lastname } : {}),
      ...(email ? { email } : {}),
      ...(companyName ? { companyName } : {}),
      ...(createdByUniqueCode ? { 'createdBy.uniqueCode': createdByUniqueCode } : {}),
      ...(status && status.length > 0 ? { 'status[]': status } : {}),
      ...(createdAtAfter ? { 'createdAt[after]': createdAtAfter } : {}),
      ...(createdAtBefore ? { 'createdAt[before]': createdAtBefore } : {}),
      ...orderParams
    } as ApiAdminwaitingListsGetCollectionParams & Record<string, unknown>;

    try {
      return await apiAdminwaitingListsGetCollection(params);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/waiting_lists')) {
        showOrvalErrorToast(error);
      }
      throw error;
    }
  }

  function waitingListFilters() {
    const firstname =
      (columnFilters.find((filter) => filter.id === 'firstname')?.value as string) ?? '';
    const lastname =
      (columnFilters.find((filter) => filter.id === 'lastname')?.value as string) ?? '';
    const email = (columnFilters.find((filter) => filter.id === 'email')?.value as string) ?? '';
    const companyName =
      (columnFilters.find((filter) => filter.id === 'companyName')?.value as string) ?? '';
    const createdByUniqueCode =
      (columnFilters.find((filter) => filter.id === 'createdBy')?.value as string) ?? '';
    const createdAtAfter =
      (columnFilters.find((filter) => filter.id === 'createdAtAfter')?.value as string) ?? '';
    const createdAtBefore =
      (columnFilters.find((filter) => filter.id === 'createdAtBefore')?.value as string) ?? '';
    const statusFilter = columnFilters.find((filter) => filter.id === 'status');
    const status = statusFilter?.value as WaitingListStatus[] | undefined;

    return {
      firstname,
      lastname,
      email,
      companyName,
      createdByUniqueCode,
      createdAtAfter,
      createdAtBefore,
      status
    };
  }

  const waitingListQuery = useQuery({
    queryKey: ['waiting_list_collection'],
    queryFn: async () => {
      return waitingListFiltersQuery(waitingListFilters(), null, null);
    }
  });

  const filterFields = useWaitingListFilterFields();

  const openProposeForm = useCallback(() => {
    setProposeFormKey((k) => k + 1);
    setIsProposeFormOpen(true);
  }, []);

  const closeProposeForm = useCallback(() => {
    setIsProposeFormOpen(false);
  }, []);

  const handleEditRow = useCallback((row: WaitingListTableRow) => {
    if (!isWaitingListRowEditable(row)) return;
    setEditWaitingListId(row.id);
  }, []);

  const columns = useWaitingListColumns({ onEdit: handleEditRow, canEdit: canEditWaitingList });

  const waitingListTablePersistenceKey = useMemo(
    () => getAdminTablePersistenceKey('waiting-list', user.data?.id),
    [user.data?.id]
  );

  const { table } = useDataTable<WaitingListTableRow, unknown>({
    data: (waitingListQuery.data?.data ?? []) as WaitingListTableRow[],
    columns,
    filterFields,
    pageCount: waitingListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      key: waitingListTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting: SortingState = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  useEffect(() => {
    if (sorting.length > 0) return;
    table.setSorting([{ id: 'createdAt', desc: true }]);
  }, [sorting.length, table]);

  function getWaitingListExportRow(row: WaitingListTableRow) {
    const sanitize = (value: string | undefined) => value?.replace(/[\r\n\t]/g, '') ?? '';

    const createdBy = row.createdBy?.user?.fullname ?? '';

    const dateStr = row.createdAt
      ? format(new Date(row.createdAt), 'dd/MM/yyyy', { locale: fr })
      : '';

    return [
      sanitize(row.lastname ?? undefined),
      sanitize(row.firstname ?? undefined),
      sanitize(row.email ?? undefined),
      sanitize(row.companyName ?? undefined),
      sanitize(createdBy),
      dateStr,
      t(row.status)
    ];
  }

  async function exportData() {
    setIsExporting(true);

    const limit = 500;
    const totalPageCount = Math.ceil((waitingListQuery.data?.pagination?.totalItems ?? 1) / limit);

    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      waitingListFiltersQuery(waitingListFilters(), i + 1, limit)
    );

    const pagesData = await Promise.all(promises);
    const allData = pagesData.flatMap((pageData) => pageData.data ?? []) as WaitingListTableRow[];

    const fields = [
      t('label.user.lastname'),
      t('label.user.firstname'),
      t('label.user.email'),
      t('label.company.name'),
      t('label.waiting.list.created.by'),
      t('label.waiting.list.request.date'),
      t('label.waiting.list.status')
    ];

    const csv = unparse({
      fields,
      data: allData.map((row) => getWaitingListExportRow(row))
    });

    const currentDate = new Date();
    const fileName = `waiting-list-export-${format(currentDate, 'yyyy-MM-dd-HHmmss')}.csv`;

    downloadCSV(csv, fileName);
    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex !== prevValues.pageIndex ||
      pageSize !== prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) !== JSON.stringify(prevValues.sorting)
    ) {
      waitingListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <div className="space-y-6">
      <span className="text-4xl font-bold">{t('admin.waiting.list.page.title')}</span>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="flex gap-2 items-center">
              <UserPlus className="w-6 h-6" />
              <p className="text-xl font-bold">{t('admin.waiting.list.cta.title')}</p>
            </h2>
            <p className="text-muted-foreground">{t('admin.waiting.list.cta.description')}</p>
          </div>

          {!isProposeFormOpen ? (
            <div className="flex mt-6">
              <Button rounded="full" type="button" onClick={openProposeForm}>
                {t('admin.waiting.list.cta.button')} <Plus />
              </Button>
            </div>
          ) : (
            <div className="pt-2 mt-8 border-t border-border">
              <WaitingListProposeForm
                key={proposeFormKey}
                onCancel={closeProposeForm}
                onSuccess={closeProposeForm}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {waitingListQuery.isLoading && (
        <DataTableSkeleton
          columnCount={9}
          cellWidths={['8rem', '8rem', '12rem', '12rem', '10rem', '8rem', '8rem', '8rem', '3rem']}
        />
      )}

      {waitingListQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
            title={t('admin.waiting.list.table.title')}
          />

          <DataTable table={table} totalItems={waitingListQuery.data?.pagination?.totalItems} />
        </>
      )}

      <WaitingListEditModal
        id={editWaitingListId}
        open={editWaitingListId !== null}
        onOpenChange={(open) => {
          if (!open) setEditWaitingListId(null);
        }}
        canManageConversion={canManageWaitingListConversion}
      />
    </div>
  );
};
